import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environment/environment';
import type { ExternalServiceDTO } from '@libs/dtos/provider/provider.dto';
import { catchError, Observable, shareReplay, tap, throwError } from 'rxjs';

export interface ProviderListResponse {
	data: { id: number; name: string; apiUrl: string | null }[];
	total: number;
}

@Injectable({
	providedIn: 'root',
})
export class ProviderService {
	private readonly baseUrl = `${environment.pathBackend}/providers`;

	/** Provider list shared by every product form opened in the session */
	private providers$: Observable<ProviderListResponse> | null = null;

	constructor(private http: HttpClient) {}

	/**
	 * Gets the list of providers (local entities), requested once and shared.
	 * The frontend does not edit providers; a failed load is retried next time.
	 */
	getProviders(): Observable<ProviderListResponse> {
		if (!this.providers$) {
			this.providers$ = this.http.get<ProviderListResponse>(this.baseUrl).pipe(
				catchError(() => throwError(() => new Error('Failed to fetch providers'))),
				tap({
					error: () => {
						this.providers$ = null;
					},
				}),
				shareReplay(1),
			);
		}
		return this.providers$;
	}

	/** One service of a provider's catalog by its code; null when the provider has no such service */
	getServiceByCode(providerId: number, code: string): Observable<ExternalServiceDTO | null> {
		const params = new HttpParams().set('providerId', providerId.toString()).set('code', code);
		return this.http.get<ExternalServiceDTO | null>(`${this.baseUrl}/services`, { params }).pipe(
			catchError(() => {
				return throwError(() => new Error('Failed to fetch service'));
			}),
		);
	}
}
